import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  IconButton,
  Menu,
  MenuItem,
  TableSortLabel
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";
import BaseModal from "./modals/BaseModal";

export interface ResultsTableProps {
  data: any[];
  columns: any[];
  sortColumn: string | null;  // Column to sort by
  sortOrder: 'asc' | 'desc';  // Sorting order
  onSortChange: (column: string, order: 'asc' | 'desc') => void;  
  onDownloadFullCSV?: () => void | Promise<void>;   
  fullDownloadLabel?: string;  
  
  serverPagination?: boolean;
  totalCount?: number;
  onPageRequest?: (page: number, pageSize: number) => void;

}

const ResultsTable = ({ data, columns,sortColumn, sortOrder, onSortChange,onDownloadFullCSV,  fullDownloadLabel,serverPagination=false, totalCount, onPageRequest }: ResultsTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  
  
  
const handleRequestSort = (column: string) => {
  const isAsc = sortColumn === column && sortOrder === "asc";
  const nextOrder = isAsc ? "desc" : "asc";
  
  setPage(0); // ✅ reset UI page 

  onSortChange(column, nextOrder);

  if (serverPagination && onPageRequest) {
    onPageRequest(0, rowsPerPage); // fetch the first backend page
  }
};





  // const paginatedRows = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const paginatedRows = React.useMemo(() => {
  if (serverPagination) return data; // backend already paginated + sorted
  if (!sortColumn) return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const copy = [...data];
  copy.sort((a, b) => {
    const av = a?.[sortColumn];
    const bv = b?.[sortColumn];
    const cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true });
    return sortOrder === "asc" ? cmp : -cmp;
  });
  
  return copy.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
}, [data, serverPagination, sortColumn, sortOrder, page, rowsPerPage]);





  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
    if (serverPagination && onPageRequest) {
    onPageRequest(newPage, rowsPerPage);
  }
  };

 const handleChangeRowsPerPage = (event: any) => {
  const newSize = parseInt(event.target.value, 10);
  setRowsPerPage(newSize);
  setPage(0);

  if (serverPagination && onPageRequest) {
    onPageRequest(0, newSize);
  }
};


  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
    handleMenuClose();
  };

  const downloadCSV = () => {
    // Create CSV content
    const headers =
      columns.length > 0
        ? columns.map((col: any) => col.name)
        : Object.keys(data[0] || {});
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            // Escape CSV values that contain commas or quotes
            if (
              typeof value === "string" &&
              (value.includes(",") ||
                value.includes('"') ||
                value.includes("\n"))
            ) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(","),
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "results_table.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  const printTableAsPdf = () => {
    const tableElement = document.querySelector(
      ".results-table",
    ) as HTMLElement;
    if (tableElement) {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Table Export</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; font-weight: bold; }
                @media print {
                  body { margin: 0; }
                  table { page-break-inside: auto; }
                  tr { page-break-inside: avoid; page-break-after: auto; }
                }
              </style>
            </head>
            <body>
              <h2>Results Table Export</h2>
              ${tableElement.outerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }
    }
    handleMenuClose();
  };

  // const headers =
  //   columns.length > 0
  //     ? columns.map((col: any) => col.name)
  //     : Object.keys(data[0] || {});

  const tableContent = (
    <TableContainer
      sx={{
        borderRadius: 2,
        backgroundColor: "transparent",
        // Remove fixed height constraints for normal view
        height: "auto",
        maxHeight: "none",
      }}
    >
      <Table
        size="small"
        sx={{ backgroundColor: "white" }}
        className="results-table"
      >
        {/* <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell key={header}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead> */}
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.name}>
                <TableSortLabel
                  active={sortColumn === column.name}
                  direction={sortColumn === column.name ? sortOrder : "asc"}
                  onClick={() => handleRequestSort(column.name)}
                  sx={{
                    "& .MuiTableSortLabel-icon": {
                      opacity: sortColumn === column.name ? 0.8 : 0,  // hidden if not active
                      transition: "opacity 0.15s ease",
                      fontSize: 18,
                      marginLeft: 0.5,
                    },
                    "&:hover .MuiTableSortLabel-icon": {
                      opacity: 0.8, // show on hover
                    },
                  }}
                >
                  {column.name}
                </TableSortLabel>

              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedRows.map((row, rowIndex) => (
            <TableRow key={row.id ?? rowIndex} hover>
              {columns.length > 0
                ? columns.map((column) => (
                    <TableCell
                      key={`${column.name}-${rowIndex}`}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {column.selector
                        ? column.selector(row)
                        : row[column.name]}
                    </TableCell>
                  ))
                : Object.values(row).map((value, cellIndex) => (
                    <TableCell key={`${rowIndex}-${cellIndex}`}>
                      {String(value)}
                    </TableCell>
                  ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={serverPagination ? (totalCount ?? 0) : data.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count}`
        }
      />
    </TableContainer>
  );

  return (
    <div>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <IconButton
          size="small"
          onClick={handleMenuClick}
          className="text-gray-600 hover:text-gray-800"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      {tableContent}

      {/* Three dots menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleFullscreenToggle}>
          <FullscreenIcon fontSize="small" sx={{ mr: 1 }} />
          Expand to Fullscreen
        </MenuItem>
        <MenuItem onClick={onDownloadFullCSV ? () => { handleMenuClose(); onDownloadFullCSV(); } : downloadCSV}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          {onDownloadFullCSV ? (fullDownloadLabel ?? "Download CSV (all data)") : "Download CSV"}
        </MenuItem>
        <MenuItem onClick={printTableAsPdf}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
          Print / Save as PDF
        </MenuItem>

      </Menu>

      {/* Fullscreen Modal */}
      <BaseModal
        open={isFullscreen}
        onClose={handleFullscreenToggle}
        title="Table View - Fullscreen"
        maxWidth="xl"
        fullWidth={true}
      >
        <Box
          sx={{
            height: "85vh",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            minHeight: "600px",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            {tableContent}
          </Box>
        </Box>
      </BaseModal>
    </div>
  );
};

export default ResultsTable;
